// =============================================================================
// VandieZ shipper-web -- KYC (/kyc)
// =============================================================================
// Two-step upload flow per backend:
//   1. POST /uploads/kyc-document  -> { storage_key }
//   2. POST /kyc/documents         -> create kyc_document row
// Then POST /kyc/submit once all required docs are uploaded.
//
// Required documents for a shipper (per backend kyc.py):
//   * aadhaar  - identity proof
//   * pan      - tax ID
// Optional but common: business_proof, address_proof.
//
// Status comes from GET /kyc/status -- we render the overall pill,
// per-doc status with admin-side rejection notes, and a primary CTA
// (upload / submit) driven by the backend's `required_actions`.
// =============================================================================

import {
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Send,
  Upload,
  XCircle,
} from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";

import { kycApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  KycDocumentType,
  KycOverallStatus,
  KycStatusDocument,
  KycStatusResponse,
} from "@/types/user";

interface DocSpec {
  type: KycDocumentType;
  label: string;
  description: string;
  required: boolean;
}

const DOC_SPECS: DocSpec[] = [
  {
    type: "aadhaar",
    label: "Aadhaar card",
    description: "Front + back, or e-Aadhaar PDF.",
    required: true,
  },
  {
    type: "pan",
    label: "PAN card",
    description: "Required for invoices and TDS compliance.",
    required: true,
  },
  {
    type: "business_proof",
    label: "Business proof",
    description: "GST certificate or partnership deed (optional).",
    required: false,
  },
  {
    type: "address_proof",
    label: "Address proof",
    description: "Recent utility bill or rent agreement (optional).",
    required: false,
  },
];

const OVERALL_LABEL: Record<KycOverallStatus, string> = {
  not_started: "Not started",
  incomplete: "Incomplete",
  ready_to_submit: "Ready to submit",
  pending_review: "Pending review",
  partially_rejected: "Action required",
  approved: "Verified",
};

const OVERALL_TONE: Record<KycOverallStatus, string> = {
  not_started: "bg-muted text-muted-foreground",
  incomplete: "bg-warning-subtle text-warning-subtle-foreground",
  ready_to_submit: "bg-primary-subtle text-primary-subtle-foreground",
  pending_review: "bg-primary-subtle text-primary-subtle-foreground",
  partially_rejected: "bg-danger-subtle text-danger-subtle-foreground",
  approved: "bg-success-subtle text-success-subtle-foreground",
};

const REVIEW_STATUS_LABEL: Record<string, string> = {
  pending: "Pending review",
  approved: "Approved",
  rejected: "Rejected",
  resubmit_requested: "Resubmit requested",
};

function latestDocFor(
  docs: KycStatusDocument[],
  type: KycDocumentType,
): KycStatusDocument | null {
  const matches = docs.filter((d) => d.document_type === type);
  if (matches.length === 0) {return null;}
  return matches[0] ?? null;
}

export function KycPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();

  const [status, setStatus] = useState<KycStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [uploading, setUploading] = useState<KycDocumentType | null>(null);

  const fileInputRefs = useRef<Partial<Record<KycDocumentType, HTMLInputElement | null>>>({});

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  async function refresh(): Promise<void> {
    if (accessToken === null) {return;}
    try {
      setIsLoading(true);
      const s = await kycApi.status(accessToken);
      setStatus(s);
      setError(null);
    } catch (err: unknown) {
      setError(
        err instanceof ApiError ? err.problem.detail : "Couldn't load KYC status.",
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function onPickFile(type: KycDocumentType, file: File): Promise<void> {
    if (accessToken === null) {return;}
    setUploading(type);
    try {
      const uploaded = await kycApi.uploadFile(accessToken, file);
      await kycApi.createDocument(accessToken, {
        storage_key: uploaded.storage_key,
        document_type: type,
      });
      toast.success(`Uploaded ${type.replace(/_/g, " ")}.`);
      await refresh();
    } catch (err: unknown) {
      toast.error(
        err instanceof ApiError ? err.problem.detail : "Upload failed.",
      );
    } finally {
      setUploading(null);
      // Clear the input so re-picking the same file fires onChange again.
      const ref = fileInputRefs.current[type];
      if (ref !== null && ref !== undefined) {
        ref.value = "";
      }
    }
  }

  async function onSubmit(): Promise<void> {
    if (accessToken === null) {return;}
    setIsSubmitting(true);
    try {
      await kycApi.submit(accessToken);
      toast.success("KYC submitted for review.");
      await refresh();
    } catch (err: unknown) {
      toast.error(
        err instanceof ApiError ? err.problem.detail : "Couldn't submit.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const canSubmit =
    status !== null &&
    (status.overall_status === "ready_to_submit" ||
      status.required_actions.includes("submit_for_review"));

  // Aggregate rejection / resubmit context so the page can surface it
  // prominently. The backend's `overall_status === "partially_rejected"`
  // captures "at least one required doc was rejected" — but we also
  // want the per-doc list so we can name what to fix.
  const rejectedDocs = (status?.documents ?? []).filter(
    (d) =>
      d.admin_review_status === "rejected" ||
      d.admin_review_status === "resubmit_requested",
  );
  const hasRejections = rejectedDocs.length > 0;
  // Submit button label changes if the user previously submitted —
  // "Resubmit for review" reads truer than "Submit for review" when
  // they're returning after a rejection.
  const submitLabel =
    status?.submitted_at !== null && status?.submitted_at !== undefined
      ? "Resubmit for review"
      : "Submit for review";

  return (
    <div className="space-y-6">
      <PageHeader
        title="KYC"
        description="Upload your Aadhaar + PAN, then submit for admin review. Approved KYC unlocks bidding and payouts."
        actions={
          status !== null ? (
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                OVERALL_TONE[status.overall_status],
              )}
            >
              {OVERALL_LABEL[status.overall_status]}
            </span>
          ) : undefined
        }
      />

      {error !== null ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {isLoading || status === null ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {hasRejections ? (
            <Card className="border-danger">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-danger-subtle-foreground">
                  <XCircle className="h-5 w-5" /> Action needed on{" "}
                  {rejectedDocs.length === 1 ? "1 document" : `${rejectedDocs.length} documents`}
                </CardTitle>
                <CardDescription>
                  The admin reviewed your submission and asked for changes.
                  Re-upload the documents below, then click <strong>Resubmit
                  for review</strong> to re-enter the queue.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {rejectedDocs.map((d) => (
                    <li
                      key={d.id}
                      // /25 not /40: the reviewer-notes line below is
                      // text-muted-foreground, which measures 4.45:1 (fails
                      // AA) over a /40 tint and 4.57:1 over /25.
                      className="rounded-md border border-danger bg-danger-subtle/25 p-3"
                    >
                      <p className="font-medium capitalize">
                        {d.document_type.replace(/_/g, " ")} —{" "}
                        <span className="text-danger-subtle-foreground">
                          {REVIEW_STATUS_LABEL[d.admin_review_status] ?? d.admin_review_status}
                        </span>
                      </p>
                      {d.admin_review_notes !== null && d.admin_review_notes.length > 0 ? (
                        <p className="mt-1 text-muted-foreground">
                          {d.admin_review_notes}
                        </p>
                      ) : (
                        <p className="mt-1 text-muted-foreground italic">
                          No notes from the reviewer — try a clearer photo,
                          well-lit, all four corners visible.
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : status.required_actions.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Next steps</CardTitle>
                <CardDescription>
                  Follow these to move your KYC forward.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {status.required_actions.map((action) => (
                    <li key={action} className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>{action.replace(/_/g, " ")}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}

          <div className="space-y-3">
            {DOC_SPECS.map((spec) => {
              const doc = latestDocFor(status.documents, spec.type);
              const isUploading = uploading === spec.type;
              return (
                <Card key={spec.type}>
                  <CardContent className="flex flex-wrap items-center gap-3 py-4">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">
                        {spec.label}
                        {spec.required ? (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (required)
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {spec.description}
                      </p>
                      {doc !== null ? (
                        <DocStatusLine doc={doc} />
                      ) : (
                        <p className="mt-1 text-xs italic text-muted-foreground">
                          Not uploaded yet.
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        ref={(el) => { fileInputRefs.current[spec.type] = el; }}
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file !== undefined) { void onPickFile(spec.type, file); }
                        }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isUploading}
                        onClick={() => {
                          fileInputRefs.current[spec.type]?.click();
                        }}
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Uploading…
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            {doc !== null ? "Replace" : "Upload"}
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {status.overall_status !== "approved" ? (
            <div className="flex items-center justify-end gap-2">
              <Button
                onClick={() => void onSubmit()}
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    {submitLabel}
                  </>
                )}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function DocStatusLine({ doc }: { doc: KycStatusDocument }): React.JSX.Element {
  const label = REVIEW_STATUS_LABEL[doc.admin_review_status] ?? doc.admin_review_status;
  let Icon = Clock;
  let tone = "text-muted-foreground";
  if (doc.admin_review_status === "approved") {
    Icon = CheckCircle2;
    tone = "text-success-subtle-foreground";
  } else if (
    doc.admin_review_status === "rejected" ||
    doc.admin_review_status === "resubmit_requested"
  ) {
    Icon = XCircle;
    tone = "text-danger-subtle-foreground";
  }
  return (
    <div className="mt-1 space-y-0.5 text-xs">
      <p className={cn("flex items-center gap-1.5", tone)}>
        <Icon className="h-3.5 w-3.5" />
        <span>{label}</span>
        {doc.document_number_masked !== null ? (
          <span className="font-mono text-muted-foreground">
            · {doc.document_number_masked}
          </span>
        ) : null}
      </p>
      {doc.admin_review_notes !== null && doc.admin_review_notes.length > 0 ? (
        <p className="text-muted-foreground">
          Reviewer: {doc.admin_review_notes}
        </p>
      ) : null}
    </div>
  );
}
